import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sprout } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/data/site";

const STORAGE_KEY = "hr_newsletter_seen";

export function NewsletterModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Wait until the visitor has made a cookie-consent choice before nagging
    // with the newsletter popup (keeps the consent banner reachable first).
    if (!localStorage.getItem("hr_consent")) return;
    const t = setTimeout(() => setOpen(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Du må samtykke til å motta nyhetsbrev."); return; }
    setSending(true);
    try {
      await api.post("/newsletter", { email, interest: "Nyhetsbrev (popup)", consent: true });
      localStorage.setItem(STORAGE_KEY, "1");
      toast.success("Takk! Du er påmeldt nyhetsbrevet.");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-olive-deep/60 backdrop-blur-sm"
          data-testid="newsletter-modal" onClick={dismiss}>
          <motion.div
            initial={{ scale: 0.92, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-cream shadow-2xl">
            <button onClick={dismiss} data-testid="newsletter-modal-close"
              className="absolute top-4 right-4 z-10 text-olive/50 hover:text-terra transition-colors">
              <X size={22} />
            </button>
            <div className="grid sm:grid-cols-[0.9fr_1.1fr]">
              <div className="hidden sm:block relative">
                <img src={ASSETS.tips} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-olive-deep/20" />
              </div>
              <div className="p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage/25 text-olive-deep">
                  <Sprout size={24} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-serif text-3xl leading-tight text-olive-deep">Bli med i bevegelsen</h3>
                <p className="mt-3 text-sm text-olive/70 leading-relaxed">
                  Meld deg på nyhetsbrevet og få tips, råd og nyheter om helse på egne premisser – rett i innboksen.
                </p>
                <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
                  <input required type="email" placeholder="Din e-post" value={email} data-testid="newsletter-modal-email"
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-full border border-olive/25 bg-white/70 px-5 py-3.5 text-sm focus:outline-none focus:border-terra transition-colors" />
                  <label className="flex items-start gap-2 text-[11px] text-olive/65 cursor-pointer">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      data-testid="newsletter-modal-consent" className="mt-0.5 accent-terra" />
                    <span>Jeg samtykker til å motta nyhetsbrev og har lest{" "}
                      <Link to="/personvern" className="underline hover:text-terra">personvernerklæringen</Link>.</span>
                  </label>
                  <button type="submit" disabled={sending} data-testid="newsletter-modal-submit"
                    className="rounded-full bg-terra px-6 py-3.5 text-cream text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-olive-deep transition-colors disabled:opacity-60">
                    {sending ? "Melder på…" : "Meld meg på"}
                  </button>
                </form>
                <button onClick={dismiss} className="mt-3 text-[11px] uppercase tracking-[0.12em] text-olive/40 hover:text-olive/70">
                  Kanskje senere
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
